// Firestore 데이터베이스 관련 함수들

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp,
  setDoc,
  orderBy
} from 'firebase/firestore';
import { db } from './config';

// 사업자 목록
export const BUSINESS_ENTITIES = [
  { id: 'aom', name: '(주)에이오엠' },
  { id: 'apartmentfull', name: '(주)아파트먼트풀' },
  { id: 'apartmentfull_foarms', name: '아파트먼트풀 포알엠에스' },
  { id: 'apartmentfull_market', name: '아파트먼트풀 마켓' }
];

// 출퇴근 기록 추가
export const addAttendanceRecord = async (userId, recordData) => {
  try {
    const docRef = await addDoc(collection(db, 'attendance'), {
      userId,
      ...recordData,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 출퇴근 기록 업데이트
export const updateAttendanceRecord = async (recordId, updateData) => {
  try {
    await updateDoc(doc(db, 'attendance', recordId), {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 출퇴근 기록 삭제
export const deleteAttendanceRecord = async (recordId) => {
  try {
    await deleteDoc(doc(db, 'attendance', recordId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 사용자의 출퇴근 기록 조회
export const getUserAttendanceRecords = async (userId) => {
  try {
    const q = query(
      collection(db, 'attendance'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const records = [];
    
    querySnapshot.forEach((doc) => {
      records.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // 클라이언트 측에서 날짜별로 정렬
    records.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // 내림차순 정렬
    });
    
    return { success: true, records };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 전체 사용자의 출퇴근 기록 조회 (관리자용)
export const getAllAttendanceRecords = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'attendance'));
    const records = [];

    querySnapshot.forEach((doc) => {
      records.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // 클라이언트 측에서 날짜별로 정렬
    records.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // 내림차순 정렬
    });

    return { success: true, records };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 월의 출퇴근 기록 조회 (월별 필터링 - 전체 기록 로드 방지)
// month: 0-indexed (0 = 1월, 11 = 12월)
export const getAttendanceRecordsByMonth = async (year, month) => {
  try {
    const paddedMonth = String(month + 1).padStart(2, '0');
    const startDate = `${year}-${paddedMonth}-01`;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    const endDate = `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-01`;

    const q = query(
      collection(db, 'attendance'),
      where('date', '>=', startDate),
      where('date', '<', endDate)
    );

    const querySnapshot = await getDocs(q);
    const records = [];
    querySnapshot.forEach((doc) => {
      records.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, records };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 날짜의 출퇴근 기록 조회
export const getAttendanceRecordByDate = async (userId, date) => {
  try {
    const q = query(
      collection(db, 'attendance'),
      where('userId', '==', userId),
      where('date', '==', date)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { 
        success: true, 
        record: {
          id: doc.id,
          ...doc.data()
        }
      };
    }
    
    return { success: true, record: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 사용자 정보 업데이트
export const updateUserInfo = async (userId, updateData) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 전체 사용자 목록 조회 (관리자용)
export const getAllUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    const users = [];
    
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, users };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 사용자 정보 조회
export const getUserInfo = async (userId) => {
  try {
    const docSnap = await getDoc(doc(db, 'users', userId));

    if (docSnap.exists()) {
      return {
        success: true,
        user: {
          id: docSnap.id,
          ...docSnap.data()
        }
      };
    }

    return { success: false, error: '사용자를 찾을 수 없습니다.' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// === 급여 관리 기능 추가 ===

// 급여 확정 처리
export const confirmMonthlySalary = async (year, month, userId, salaryData) => {
  try {
    const confirmationId = `${year}-${String(month).padStart(2, '0')}-${userId}`;
    await setDoc(doc(db, 'salaryConfirmation', confirmationId), {
      year,
      month,
      userId,
      ...salaryData,
      confirmedAt: serverTimestamp(),
      status: 'confirmed'
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 월별 급여 확정 내역 조회
export const getConfirmedSalaries = async (year, month) => {
  try {
    const q = query(
      collection(db, 'salaryConfirmation'),
      where('year', '==', year),
      where('month', '==', month),
      where('status', '==', 'confirmed')
    );

    const querySnapshot = await getDocs(q);
    const salaries = [];

    querySnapshot.forEach((doc) => {
      salaries.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, salaries };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 직원 급여 정보 저장/업데이트
export const saveEmployeeSalary = async (employeeId, salaryInfo) => {
  try {
    await setDoc(doc(db, 'employeeSalary', employeeId), {
      ...salaryInfo,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 직원 급여 정보 조회
export const getEmployeeSalary = async (employeeId) => {
  try {
    const docSnap = await getDoc(doc(db, 'employeeSalary', employeeId));

    if (docSnap.exists()) {
      return {
        success: true,
        salary: {
          id: docSnap.id,
          ...docSnap.data()
        }
      };
    }

    return { success: true, salary: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 전체 직원 급여 정보 조회
export const getAllEmployeeSalaries = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'employeeSalary'));
    const salaries = [];

    querySnapshot.forEach((doc) => {
      salaries.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, salaries };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 야근 기록 추가
export const addOvertimeRecord = async (employeeId, overtimeData) => {
  try {
    const docRef = await addDoc(collection(db, 'overtimeRecords'), {
      employeeId,
      ...overtimeData,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 야근 기록 조회
export const getOvertimeRecords = async (employeeId, year, month) => {
  try {
    const q = query(
      collection(db, 'overtimeRecords'),
      where('employeeId', '==', employeeId),
      where('year', '==', year),
      where('month', '==', month)
    );

    const querySnapshot = await getDocs(q);
    const records = [];

    querySnapshot.forEach((doc) => {
      records.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, records };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// === 개인정보 관리 기능 추가 ===

// 직원 개인정보 저장/업데이트
export const saveEmployeePersonalInfo = async (employeeId, personalInfo) => {
  try {
    await setDoc(doc(db, 'employeePersonalInfo', employeeId), {
      ...personalInfo,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 직원 개인정보 조회
export const getEmployeePersonalInfo = async (employeeId) => {
  try {
    const docSnap = await getDoc(doc(db, 'employeePersonalInfo', employeeId));

    if (docSnap.exists()) {
      return {
        success: true,
        info: docSnap.data()
      };
    }

    return { success: true, info: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// === 정규직원 데이터 관리 ===

// 정규직원 급여 데이터 저장
export const saveRegularEmployeeData = async (employeeData) => {
  try {
    const docRef = await addDoc(collection(db, 'regularEmployees'), {
      ...employeeData,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 정규직원 목록 조회
export const getRegularEmployees = async (year, month) => {
  try {
    const q = query(
      collection(db, 'regularEmployees'),
      where('year', '==', year),
      where('month', '==', month)
    );

    const querySnapshot = await getDocs(q);
    const employees = [];

    querySnapshot.forEach((doc) => {
      employees.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, employees };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 정규직원 삭제
export const deleteRegularEmployee = async (employeeId) => {
  try {
    await deleteDoc(doc(db, 'regularEmployees', employeeId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// === 정규직원 마스터 데이터 관리 (새로운 구조) ===

// 정규직원 마스터 데이터 저장/업데이트
export const saveRegularEmployeeMaster = async (employeeId, employeeData) => {
  try {
    // 사업자 정보가 없으면 기본값 설정
    const dataToSave = {
      ...employeeData,
      businessEntity: employeeData.businessEntity || BUSINESS_ENTITIES[0].id,
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'regularEmployeesMaster', employeeId), dataToSave, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 정규직원 마스터 데이터 목록 조회
export const getRegularEmployeesMaster = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'regularEmployeesMaster'));
    const employees = [];

    querySnapshot.forEach((doc) => {
      employees.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // 이름순으로 정렬
    employees.sort((a, b) => a.name.localeCompare(b.name, 'ko'));

    return { success: true, employees };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============= 비과세 항목 관리 =============

// 비과세 항목 조회 (사업자별)
export const getNonTaxableItems = async (businessEntity) => {
  try {
    const q = query(
      collection(db, 'nonTaxableItems'),
      where('businessEntity', '==', businessEntity),
      where('active', '==', true),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const items = [];

    snapshot.forEach((doc) => {
      items.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, items };
  } catch (error) {
    console.error('비과세 항목 조회 오류:', error);
    return { success: false, error: error.message, items: [] };
  }
};

// 비과세 항목 추가
export const addNonTaxableItem = async (businessEntity, itemData, userId) => {
  try {
    const docRef = await addDoc(collection(db, 'nonTaxableItems'), {
      businessEntity,
      name: itemData.name,
      active: true,
      createdAt: serverTimestamp(),
      createdBy: userId
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('비과세 항목 추가 오류:', error);
    return { success: false, error: error.message };
  }
};

// 비과세 항목 삭제 (비활성화)
export const deleteNonTaxableItem = async (itemId) => {
  try {
    await updateDoc(doc(db, 'nonTaxableItems', itemId), {
      active: false,
      deletedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('비과세 항목 삭제 오류:', error);
    return { success: false, error: error.message };
  }
};

// 정규직원 마스터 데이터 삭제
export const deleteRegularEmployeeMaster = async (employeeId) => {
  try {
    await deleteDoc(doc(db, 'regularEmployeesMaster', employeeId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 정규직원 월별 급여 데이터 저장
export const saveRegularEmployeeSalaryData = async (year, month, salaryDataArray, businessEntity) => {
  try {
    const docId = `${businessEntity}_${year}-${String(month).padStart(2, '0')}`;
    await setDoc(doc(db, 'regularEmployeesSalary', docId), {
      year,
      month,
      salaryData: salaryDataArray,
      businessEntity,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 정규직원 월별 급여 데이터 조회
export const getRegularEmployeeSalaryData = async (year, month, businessEntity) => {
  try {
    // 새로운 형식 (사업자 포함)
    const newDocId = `${businessEntity}_${year}-${String(month).padStart(2, '0')}`;
    const newDocSnap = await getDoc(doc(db, 'regularEmployeesSalary', newDocId));

    // 기존 형식 (사업자 없음) - 첫 번째 사업자에서만 조회
    const oldDocId = `${year}-${String(month).padStart(2, '0')}`;
    const oldDocSnap = businessEntity === BUSINESS_ENTITIES[0].id
      ? await getDoc(doc(db, 'regularEmployeesSalary', oldDocId))
      : null;

    let salaryData = [];

    // 새로운 형식 데이터가 있으면 추가
    if (newDocSnap && newDocSnap.exists()) {
      salaryData = [...(newDocSnap.data().salaryData || [])];
    }

    // 기존 형식 데이터가 있으면 추가 (첫 번째 사업자에만)
    if (oldDocSnap && oldDocSnap.exists()) {
      const oldData = oldDocSnap.data().salaryData || [];
      // 중복 방지를 위해 이름으로 필터링
      const existingNames = new Set(salaryData.map(emp => emp.name));
      const uniqueOldData = oldData.filter(emp => !existingNames.has(emp.name));
      salaryData = [...salaryData, ...uniqueOldData];
    }

    return {
      success: true,
      salaryData: salaryData
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 정규직원 급여 확정
export const confirmRegularEmployeeSalary = async (year, month, employeeId, salaryData) => {
  try {
    const confirmationId = `${year}-${String(month).padStart(2, '0')}-regular-${employeeId}`;
    await setDoc(doc(db, 'salaryConfirmation', confirmationId), {
      year,
      month,
      employeeId,
      employeeType: 'regular',
      ...salaryData,
      confirmedAt: serverTimestamp(),
      status: 'confirmed'
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const revertRegularEmployeeSalaryConfirmation = async (year, month, employeeId, revertedBy) => {
  try {
    const confirmationId = `${year}-${String(month).padStart(2, '0')}-regular-${employeeId}`;
    const confirmationRef = doc(db, 'salaryConfirmation', confirmationId);
    const confirmationSnap = await getDoc(confirmationRef);

    if (!confirmationSnap.exists()) {
      return { success: false, error: 'confirmation_not_found' };
    }

    await updateDoc(confirmationRef, {
      status: 'reverted',
      revertedAt: serverTimestamp(),
      revertedBy: revertedBy || null
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 직원의 사업자 변경
export const updateEmployeeBusinessEntity = async (employeeId, newBusinessEntity) => {
  try {
    // regularEmployeesMaster에서 직원 정보 업데이트
    await updateDoc(doc(db, 'regularEmployeesMaster', employeeId), {
      businessEntity: newBusinessEntity,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 파트타이머 수기 입력 저장
export const savePartTimerManualEntry = async (entryData, businessEntity) => {
  try {
    const docRef = await addDoc(collection(db, 'partTimerManualEntries'), {
      ...entryData,
      businessEntity,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 파트타이머 수기 입력 데이터 조회
export const getPartTimerManualEntries = async (year, month, businessEntity) => {
  try {
    // 사업자가 있는 데이터 조회
    const qWithBusiness = query(
      collection(db, 'partTimerManualEntries'),
      where('year', '==', year),
      where('month', '==', month),
      where('businessEntity', '==', businessEntity)
    );

    const entriesWithBusiness = [];
    const querySnapshotWithBusiness = await getDocs(qWithBusiness);
    querySnapshotWithBusiness.forEach((doc) => {
      entriesWithBusiness.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // 첫 번째 사업자의 경우, 사업자 정보가 없는 기존 데이터도 조회
    let entriesWithoutBusiness = [];
    if (businessEntity === BUSINESS_ENTITIES[0].id) {
      const qWithoutBusiness = query(
        collection(db, 'partTimerManualEntries'),
        where('year', '==', year),
        where('month', '==', month)
      );

      const querySnapshotWithoutBusiness = await getDocs(qWithoutBusiness);
      querySnapshotWithoutBusiness.forEach((doc) => {
        const data = doc.data();
        // businessEntity가 없거나 undefined인 경우만 포함
        if (!data.businessEntity) {
          entriesWithoutBusiness.push({
            id: doc.id,
            ...data,
            businessEntity: BUSINESS_ENTITIES[0].id // 기본 사업자 할당
          });
        }
      });
    }

    // 두 배열을 합치고 중복 제거
    const allEntries = [...entriesWithBusiness, ...entriesWithoutBusiness];
    const uniqueEntries = Array.from(new Map(allEntries.map(item => [item.id, item])).values());

    return { success: true, entries: uniqueEntries };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 마이그레이션: 기존 직원들에게 기본 사업자 할당
export const migrateEmployeesToDefaultBusiness = async () => {
  try {
    const masterResult = await getRegularEmployeesMaster();
    if (!masterResult.success) {
      return { success: false, error: '마스터 데이터 조회 실패' };
    }

    const employees = masterResult.employees;
    const defaultBusinessEntity = BUSINESS_ENTITIES[0].id; // 첫 번째 사업자를 기본값으로

    let updatedCount = 0;

    for (const employee of employees) {
      if (!employee.businessEntity) {
        try {
          await updateDoc(doc(db, 'regularEmployeesMaster', employee.id), {
            businessEntity: defaultBusinessEntity,
            updatedAt: serverTimestamp()
          });
          updatedCount++;
        } catch (error) {
          console.error(`직원 ${employee.name} 업데이트 실패:`, error);
        }
      }
    }

    return {
      success: true,
      message: `${updatedCount}명의 직원에게 기본 사업자(${BUSINESS_ENTITIES[0].name})가 할당되었습니다.`
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== 파트타이머 마스터 관리 =====

// 파트타이머 마스터 데이터 저장
export const savePartTimerMaster = async (partTimerId, partTimerData) => {
  try {
    await setDoc(doc(db, 'partTimersMaster', partTimerId), {
      ...partTimerData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 파트타이머 마스터 데이터 전체 조회
export const getPartTimersMaster = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'partTimersMaster'));
    const partTimers = [];

    querySnapshot.forEach((doc) => {
      partTimers.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, partTimers };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 파트타이머 마스터 데이터 삭제
export const deletePartTimerMaster = async (partTimerId) => {
  try {
    await deleteDoc(doc(db, 'partTimersMaster', partTimerId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== 공휴일 관리 =====

// 공휴일 추가
export const addHoliday = async (holidayData) => {
  try {
    const docRef = await addDoc(collection(db, 'holidays'), {
      ...holidayData,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 공휴일 삭제
export const deleteHoliday = async (holidayId) => {
  try {
    await deleteDoc(doc(db, 'holidays', holidayId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 연도의 공휴일 조회
export const getHolidaysByYear = async (year) => {
  try {
    const q = query(
      collection(db, 'holidays'),
      where('year', '==', year)
    );
    const querySnapshot = await getDocs(q);
    const holidays = [];

    querySnapshot.forEach((doc) => {
      holidays.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, holidays };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 날짜가 공휴일인지 확인
export const isHoliday = async (date) => {
  try {
    const q = query(
      collection(db, 'holidays'),
      where('date', '==', date)
    );
    const querySnapshot = await getDocs(q);
    return { success: true, isHoliday: !querySnapshot.empty };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 특정 날짜의 공휴일 정보 조회
export const getHolidayByDate = async (date) => {
  try {
    const q = query(
      collection(db, 'holidays'),
      where('date', '==', date)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        success: true,
        holiday: {
          id: doc.id,
          ...doc.data()
        }
      };
    }

    return { success: true, holiday: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 공휴일 등록 시 해당 날짜의 출퇴근 기록에 공휴일 플래그 추가
export const updateAttendanceRecordsWithHoliday = async (date, isHoliday) => {
  try {
    const q = query(
      collection(db, 'attendance'),
      where('date', '==', date)
    );
    const querySnapshot = await getDocs(q);

    const updatePromises = [];
    querySnapshot.forEach((docSnapshot) => {
      const recordRef = doc(db, 'attendance', docSnapshot.id);
      updatePromises.push(
        updateDoc(recordRef, {
          isHoliday: isHoliday,
          updatedAt: serverTimestamp()
        })
      );
    });

    await Promise.all(updatePromises);

    return {
      success: true,
      updatedCount: updatePromises.length,
      message: `${updatePromises.length}건의 출퇴근 기록에 공휴일 정보가 반영되었습니다.`
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};